import React from "react";
import { Box, IconButton, TextField, Tooltip } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

type DateRange = {
  startDate: string;
  endDate: string;
};

const DATE_FORMAT = "YYYY-MM-DD";

const toDayjs = (value: string): Dayjs | null => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const toStringDate = (value: Dayjs | null): string =>
  value ? value.format(DATE_FORMAT) : "";

const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  startLabel = "From",
  endLabel = "To",
}: {
  startDate: string;
  endDate: string;
  onChange: (range: DateRange) => void;
  startLabel?: string;
  endLabel?: string;
}): JSX.Element => {
  const showClear = Boolean(startDate || endDate);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <DatePicker
          label={startLabel}
          value={toDayjs(startDate)}
          onChange={(value) =>
            onChange({ startDate: toStringDate(value), endDate })
          }
          inputFormat={DATE_FORMAT}
          renderInput={(params) => (
            <TextField {...params} size="small" sx={{ width: 150 }} />
          )}
        />

        <DatePicker
          label={endLabel}
          value={toDayjs(endDate)}
          onChange={(value) =>
            onChange({ startDate, endDate: toStringDate(value) })
          }
          inputFormat={DATE_FORMAT}
          renderInput={(params) => (
            <TextField {...params} size="small" sx={{ width: 150 }} />
          )}
        />

        {showClear ? (
          <Tooltip title="Clear dates">
            <IconButton
              size="small"
              onClick={() => onChange({ startDate: "", endDate: "" })}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
